export type MarionAnimationTargetGroup = {
  group: string;
  partIds: string[];
};

export type MarionAnimationDefinition = {
  type: string;
  targetGroup?: string | undefined;
  partIds: string[];
  targetGroups?: MarionAnimationTargetGroup[] | undefined;
  fps?: number | undefined;
  loop?: boolean | undefined;
  durationSeconds?: number | undefined;
};

export type MarionVisibilityPart = {
  id: string;
  group?: string | undefined;
  visible: boolean;
};

export function readAnimationTargetGroups(
  value: unknown,
): MarionAnimationTargetGroup[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const groups = value.flatMap((entry): MarionAnimationTargetGroup[] => {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) return [];
    const record = entry as Record<string, unknown>;
    const group = typeof record["group"] === "string" ? record["group"] : undefined;
    const partIds = Array.isArray(record["part_ids"])
      ? record["part_ids"].flatMap((partId) =>
          typeof partId === "string" && partId.length > 0 ? [partId] : [],
        )
      : [];
    if (group === undefined || partIds.length === 0) return [];
    return [{ group, partIds }];
  });
  return groups.length > 0 ? groups : undefined;
}

export function getAnimationPartIdsForGroup(
  animation: MarionAnimationDefinition,
  group: string,
): string[] | undefined {
  const fromMulti = animation.targetGroups?.find((entry) => entry.group === group)?.partIds;
  if (fromMulti !== undefined) return fromMulti;
  if (animation.targetGroup === group && animation.partIds.length > 0) {
    return animation.partIds;
  }
  return undefined;
}

export function resolveSequencePartId(partIds: string[], frameIndex: number): string {
  return partIds[frameIndex % partIds.length] ?? partIds[0] ?? "";
}

export function resolveGroupAnimation(
  animations: Record<string, MarionAnimationDefinition> | undefined,
  animationId: string,
  expectedType: string,
): MarionAnimationDefinition | null {
  const animation = animations?.[animationId];
  if (animation === undefined || animation.type !== expectedType) return null;
  const hasPrimaryTarget =
    animation.targetGroup !== undefined && animation.partIds.length > 0;
  const hasMultiTarget =
    animation.targetGroups !== undefined &&
    animation.targetGroups.some((entry) => entry.partIds.length > 0);
  if (!hasPrimaryTarget && !hasMultiTarget) return null;
  return animation;
}

export function isMarionPartVisible(input: {
  part: MarionVisibilityPart;
  talkAnimation: MarionAnimationDefinition | null;
  blinkAnimation: MarionAnimationDefinition | null;
  talkFrameIndex: number;
  blinkFrameIndex: number;
  active: boolean;
  expressionGroups: Record<string, string> | null;
}): boolean {
  const { part, talkAnimation, blinkAnimation, talkFrameIndex, blinkFrameIndex, active } = input;

  if (part.group === undefined) {
    return part.visible;
  }

  if (talkAnimation !== null) {
    const talkPartIds = getAnimationPartIdsForGroup(talkAnimation, part.group);
    if (talkPartIds !== undefined && talkPartIds.includes(part.id)) {
      const frameIndex = active ? talkFrameIndex : 0;
      return part.id === resolveSequencePartId(talkPartIds, frameIndex);
    }
  }

  const expressionPartId = input.expressionGroups?.[part.group];
  const expressionLocksBlink =
    expressionPartId !== undefined &&
    (expressionPartId.endsWith("_closed") || expressionPartId.endsWith("_half"));

  if (expressionLocksBlink) {
    return part.id === expressionPartId;
  }

  if (blinkAnimation !== null) {
    const blinkPartIds = getAnimationPartIdsForGroup(blinkAnimation, part.group);
    if (blinkPartIds !== undefined && blinkPartIds.includes(part.id)) {
      return part.id === resolveSequencePartId(blinkPartIds, blinkFrameIndex);
    }
  }

  if (expressionPartId !== undefined) {
    return part.id === expressionPartId;
  }

  return part.visible;
}

export function filterVisibleMarionParts<T extends MarionVisibilityPart>(input: {
  parts: T[];
  talkAnimation: MarionAnimationDefinition | null;
  blinkAnimation: MarionAnimationDefinition | null;
  talkFrameIndex: number;
  blinkFrameIndex: number;
  active: boolean;
  expressionGroups: Record<string, string> | null;
}): T[] {
  return input.parts.filter((part) =>
    isMarionPartVisible({
      part,
      talkAnimation: input.talkAnimation,
      blinkAnimation: input.blinkAnimation,
      talkFrameIndex: input.talkFrameIndex,
      blinkFrameIndex: input.blinkFrameIndex,
      active: input.active,
      expressionGroups: input.expressionGroups,
    }),
  );
}
